import React, {createRef} from 'react'

import DNDComponent from "../../../../../core/gd-modules/dnd/dnd-component"
import DNDComponent2, {DNDComponent2Props} from "../../../../../core/gd-modules/dnd/dnd-component-2"
import {DraggedOverPosition} from "../../../../../core/gd-modules/dnd/@types"
import {addClass, removeClass} from "../../../../../core/gd/utils/dom-and-react/react-utils"
import menuProjects_dropRect from "./drop-rect"
import {
    MENU_PROJECTS_DND_DRAGGED_OUT,
    MENU_PROJECTS_DND_HOVER,
    MenuProjectsDndHoverParams
} from "./@events"
import MenuProjectDnd, {MenuProjectDndProps} from "./menu-project-dnd"

type Props = {
    children?: any
} & DNDComponent2Props & MenuProjectDndProps

// const debug = "MenuProjectDndItem"
const debug = false

// Wraps a single project row. It renders the row itself rather than a wrapper div, so componentEl is
// the row element - which is what the three zone hit test measures and what the insertion line draws on.
export default class MenuProject_DndItem extends DNDComponent2<Props> {

    private dndHandler          : MenuProjectDnd
    private draggedOver?        : DraggedOverPosition
    private suppressNextClick   : boolean = false
    private refChild            : any = createRef()

    static defaultProps = _.assign({}, DNDComponent.defaultProps, {
        dragTriggerDelta    : 5,
        dndZIndex           : 1,
    })

    constructor(props: Props) {
        super(props)

        this.dndHandler = new MenuProjectDnd(props)
    }

    componentDidMount() {
        super.componentDidMount()

        gd.bus.subscribe(MENU_PROJECTS_DND_HOVER, this, this.onHover)
        gd.bus.subscribe(MENU_PROJECTS_DND_DRAGGED_OUT, this, this.onDragOut)

        // GOTCHA (Firefox): the `pointer-events: none` set in render() is not enough to swallow the click
        // that follows a drop. Gecko captures the mouse on the element the drag started on, so mouseup
        // (and the resulting click) is retargeted back to it regardless of pointer-events, and the child
        // component's onClick fires. Chrome/Safari re-hit-test on mouseup and never get there.
        this.componentEl?.addEventListener("click", this._onClickCapture, true)
    }

    componentWillUnmount() {
        // before super - it drops the componentEl reference
        this.componentEl?.removeEventListener("click", this._onClickCapture, true)

        gd.bus.unsubscribe(this)

        super.componentWillUnmount()
    }

    componentDidUpdate(prevProps: Props) {
        this.dndHandler.props = this.props
    }

    // GOTCHA: rename makes the row render a different root element (NavigationMenuItemProjectRename),
    // and every DOM listener in the DNDComponent chain is bound once to the componentEl found on mount.
    // Without this the restored row after rename has no mousedown/mouseenter listeners and ignores DnD
    _onRootNodeChange = () => {
        this.componentWillUnmount()
        this.componentDidMount()

        return
    }

    _onClickCapture = (e: MouseEvent) => {
        if (!this.suppressNextClick) {
            return
        }

        this.suppressNextClick = false

        debug && console.log(debug, "_onClickCapture suppressed", this.props.project.name)

        e.stopPropagation()
        e.preventDefault()
    }

    // the insertion line only marks a drop BETWEEN rows - "into" is shown on the drag preview instead,
    // so CENTER and an expanded row's BOTTOM (also "into" - see MenuProjectDnd._isDropInto) both fall
    // through to the clearing branch rather than switching on the raw TOP/BOTTOM position
    onHover = (data: MenuProjectsDndHoverParams) => {
        if (data.project?.id === this.props.project.id) {
            this.draggedOver = data.draggedOver

            switch (data.isDropInto ? undefined : this.draggedOver) {
                case DraggedOverPosition.TOP:
                    addClass(this, "hover-top")
                    removeClass(this, "hover-bottom")
                    break

                case DraggedOverPosition.BOTTOM:
                    removeClass(this, "hover-top")
                    addClass(this, "hover-bottom")
                    break

                default:
                    removeClass(this, "hover-top")
                    removeClass(this, "hover-bottom")
            }
        } else if (!!this.draggedOver) {
            // hover moved to another row - drop our own line, and the handler's zone with it
            this.draggedOver = undefined
            this.dndHandler.resetDraggedOver()
            removeClass(this, "hover-top")
            removeClass(this, "hover-bottom")
        }
    }

    onDragOut = (data: MenuProjectsDndHoverParams) => {
        if (data.project?.id !== this.props.project.id) {
            return
        }

        this.draggedOver = undefined
        removeClass(this, "hover-top")
        removeClass(this, "hover-bottom")
    }

    getDNDId = () => {
        return this.dndHandler.getDNDId()
    }

    getDropRect() {
        return menuProjects_dropRect(this.componentEl.getBoundingClientRect())
    }

    // the row's padding-left is its tree indentation (skin/ui5/navigation/menu/item/project.styles.scss,
    // level-N) - the drag preview pill starts at the indent, not at the row's edge
    getInitialRect() {
        const rect = super.getInitialRect()
        rect.left += parseFloat(getComputedStyle(this.componentEl).paddingLeft)

        return rect
    }

    canDrag(e: MouseEvent) {
        // refChild.canDrag is how the row vetoes dragging while it is being renamed inline
        const childAllows = _.isNil(this.refChild.current?.canDrag) || this.refChild.current?.canDrag

        return this.dndHandler.canDrag(super.canDrag(e) && childAllows)
    }

    canDrop(draggedItem: any) {
        return this.dndHandler.canDrop(super.canDrop(draggedItem), draggedItem)
    }

    onMouseDown(e: MouseEvent, callback: func) {
        this.suppressNextClick = false // new gesture - drop any flag left over from a drag that ended without a click

        e.stopPropagation() // to stop on the topmost item in the nested tree

        return super.onMouseDown(e, callback)
    }

    onDraggedOut(draggedItem: any, isOnDrop: boolean) {
        super.onDraggedOut(draggedItem, isOnDrop)
        this.dndHandler.onDraggedOut(draggedItem, isOnDrop)
    }

    onDraggingOver(draggedItem: any, mouseEvent: any) {
        super.onDraggingOver(draggedItem, mouseEvent)
        this.dndHandler.onDraggingOver(draggedItem, mouseEvent, this.getDropRect())
    }

    onDragEnd(mouseEvent: MouseEvent, dx: number, dy: number, dropResult: any) {
        super.onDragEnd(mouseEvent, dx, dy, dropResult)

        this.suppressNextClick = true // see _onClickCapture

        // onDragStart's onMouseLeave() call rendered this row with pointer-events: none (see render()
        // below) - a real move gets that cleared for free by the BUS_TREE_CHANGE re-render, but a
        // no-op drop (self/rejected/same place) never fires one, so force it here or the row is stuck
        // unresponsive to every mouse event, including the next drag, until something else re-renders it
        this.forceUpdate()

        this.dndHandler.onDragEnd(dropResult)
    }

    onDrop(draggedItem: any, dropResult: any) {
        const res = this.dndHandler.onDrop(draggedItem, dropResult)

        // onDraggedOut(isOnDrop) kept the zone for onDrop - it is stale from here on, and the next drag
        // entering this row in the same zone would be deduped away and show no line
        this.dndHandler.resetDraggedOver()

        return res
    }

    render() {
        const isDragging = this.isDraggedItem(this.getDNDId())

        const styles: any = {}
        if (isDragging) {
            styles.pointerEvents = 'none'   // to prevent click in child component on drag end
        }

        return (
            <>
                {React.Children.map(this.props.children, child =>
                    React.cloneElement(child, {
                        ref                 : this.refChild,
                        dndIsDragged        : isDragging,
                        style               : styles,
                        onRootNodeChange    : this._onRootNodeChange,
                    }))}
            </>
        )
    }
}
